"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Peer = void 0;
const mediasoup_client_1 = require("mediasoup-client");
// eslint-disable-next-line import/no-cycle
const adapter_1 = require("../../../api/adapter");
const feature_1 = require("../../../api/adapter/features/feature");
const error_1 = require("../../../api/error");
const errors_1 = require("../../errors");
const errors_deprecated_1 = require("../../errors-deprecated");
const helper_1 = require("../../player/helper");
const context_1 = require("../../utils/context/context");
const event_emitter_1 = require("../../utils/events/event-emitter");
const common_1 = require("./common");
const device_1 = require("./device");
const messageList_1 = require("./messageList");
class Peer extends event_emitter_1.ObservableEventEmitter {
    constructor(ctx, call) {
        super();
        this._closed = false;
        this._iceTimeouts = new Map();
        this._mediasoupDevice = null;
        this._sendTransport = null;
        this._recvTransport = null;
        this._producers = new Map();
        this._consumerStatIntervals = new Map();
        this._delayVideoStart = false;
        this.peers = new Map();
        // transport locks used to determine if a transport is ready
        this.sendLock = null;
        this.recvLock = null;
        this.ctx = ctx;
        this.call = call;
        (0, context_1.onceCanceled)(ctx).then(this.dispose);
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()");
        const transport = this.call.transport;
        transport.on("message:newPeers", this._handleNewPeers);
        transport.on("message:consumerSources", this._handleConsumerSources);
        transport.on("message:kicked", this._handleAccessDenied);
        transport.on("message:producerstats", this._handleProducerStats);
        this.addInnerDisposer(() => {
            transport.off("message:newPeers", this._handleNewPeers);
            transport.off("message:consumerSources", this._handleConsumerSources);
            transport.off("message:kicked", this._handleAccessDenied);
            transport.off("message:producerstats", this._handleProducerStats);
        });
        this._peerEvent("newConsumer", this._onNewConsumer.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUNewConsumerEventError("unable to handle `newConsumer` peer event", {
                peer: this,
                request: {
                    id: request.id,
                    kind: request.kind,
                    producerPaused: request.producerPaused,
                    producerId: request.producerId,
                    rtpParameters: request.rtpParameters,
                },
                inner: err instanceof Error ? err : null,
            }));
        });
        this._peerEvent("peerClosed", this._onPeerClosed.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUPeerClosedEventError("unable to handle `peerClosed` peer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._peerEvent("switchConsumerTrack", this._onSwitchConsumerTrack.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUSwitchConsumerTrackEventError("unable to handle `switchConsumerTrack` peer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._consumerEvent("consumerClosed", this._onConsumerClosed.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerClosedEventError("unable to handle `consumerClosed` consumer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._consumerEvent("consumerPaused", this._onConsumerPaused.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerPausedEventError("unable to handle `consumerPaused` consumer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._consumerEvent("consumerResumed", this._onConsumerResumed.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerResumedEventError("unable to handle `consumerResumed` consumer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._consumerEvent("consumerScore", this._onConsumerScore.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerScoreEventError("unable to handle `consumerScore` consumer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._consumerEvent("consumerLayersChanged", this._onConsumerLayersChanged.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerLayersChangedEventError("unable to handle `consumerLayersChanged` consumer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._producerEvent("producerClosed", this._onProducerClosed.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUProducerClosedEventError("unable to handle `producerClosed` producer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._producerEvent("producerPaused", this._onProducerPaused.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUProducerPausedEventError("unable to handle `producerPaused` producer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this._producerEvent("producerResumed", this._onProducerResumed.bind(this), (err, request) => {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUProducerResumedEventError("unable to handle `producerResumed` producer event", {
                peer: this,
                request: request,
                inner: err instanceof Error ? err : null,
            }));
        });
        this.call.transport.on("message:createSendWebRtcTransport", this.handleCreateSendTransport);
        this.call.transport.on("message:createReceiveWebRtcTransport", this.handleCreateReceiveTransport);
        this.addInnerDisposer(() => {
            this.call.transport.off("message:createSendWebRtcTransport", this.handleCreateSendTransport);
            this.call.transport.off("message:createReceiveWebRtcTransport", this.handleCreateReceiveTransport);
        });
        this.addInnerDisposer(this.clear);
    }
    _handleConsumerSources(request) {
        try {
            this._onConsumerSources(request);
        }
        catch (err) {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUConsumerSourcesEventError("unable to handle `newPeers` peer event", {
                peer: this,
                request,
                inner: err instanceof Error ? err : null,
            }));
        }
    }
    async _handleAccessDenied(request) {
        this.call.emit("CALL_ACCESS_DENIED", request);
    }
    _handleProducerStats(request) {
        this.call.emit("CALL_PRODUCER_STATS", request.producerStats);
    }
    _handleNewPeers(request) {
        try {
            this._onNewPeers(request);
        }
        catch (err) {
            this.emitErrorDeprecated(new errors_deprecated_1.SFUNewPeersEventError("unable to handle `newPeers` peer event", {
                peer: this,
                request,
                inner: err instanceof Error ? err : null,
            }));
        }
    }
    async _setup(routerRtpCapabilities) {
        // TODO: create transports when needed - via behaviours and client action???
        // try {
        this._mediasoupDevice = new device_1.ExtendedDevice(adapter_1.device);
        await this._mediasoupDevice.load({ routerRtpCapabilities });
        // TODO: switch to using permissions
        // create webrtc transports
        let wait = [];
        if (Object.keys(this.call.streams).length > 0) {
            wait.push(this._createSendTransport());
        }
        // create receive transport
        wait.push(this._createReceiveTransport());
        await Promise.all(wait);
        this.call.emit("CALL_SET_MEDIA_CAPABILITIES", {
            canSendMic: this._mediasoupDevice.canProduce("audio"),
            canSendWebcam: this._mediasoupDevice.canProduce("video"),
        });
        // create producers for the streams
        wait = [];
        for (const s of this.call.streamsIterator()) {
            if (s.hasVideoStreamTrack && !this._delayVideoStart && this.call.hasPermission(common_1.PERMISSIONS.STREAM_VIDEO)) {
                wait.push((async () => {
                    var _a;
                    try {
                        if ((_a = this._mediasoupDevice) === null || _a === void 0 ? void 0 : _a.canProduce("video")) {
                            await s.enableVideo();
                        }
                    }
                    catch (err) {
                        const msg = err instanceof Error ? err.message : "unknown error";
                        this.ctx.logger.warn("Unable to set video producer", { err: msg });
                        this.call._addMessage(messageList_1.TROUBLESHOOTING.NO_WEBCAM);
                    }
                })());
            }
            if (s.hasAudioStreamTrack && this.call.hasPermission(common_1.PERMISSIONS.STREAM_AUDIO)) {
                wait.push((async () => {
                    var _a;
                    try {
                        if ((_a = this._mediasoupDevice) === null || _a === void 0 ? void 0 : _a.canProduce("audio")) {
                            await s.enableAudio();
                        }
                    }
                    catch (err) {
                        const msg = err instanceof Error ? err.message : "unknown error";
                        this.ctx.logger.warn("Unable to set audio producer", { err: msg });
                        this.call._addMessage(messageList_1.TROUBLESHOOTING.NO_MIC);
                    }
                })());
            }
        }
        await Promise.all(wait);
        // } catch (err) {
        //   if (this.sendLock != null) {
        //     try {
        //       this.sendLock.reject();
        //     } catch (ignore) {
        //       // pass
        //     }
        //   }
        //   if (this.recvLock != null) {
        //     try {
        //       this.recvLock.reject();
        //     } catch (ignore) {
        //       // pass
        //     }
        //   }
        //   if (this._recvTransport != null) {
        //     try {
        //       this._recvTransport.close();
        //     } catch (ignore) {
        //       // pass
        //     }
        //   }
        //   if (this._sendTransport != null) {
        //     try {
        //       this._sendTransport.close();
        //     } catch (ignore) {
        //       // pass
        //     }
        //   }
        //   this.emitErrorDeprecated(
        //     new MediasoupSetupError("ms3._setup: error during setup", {
        //       critical: true,
        //       inner: err instanceof Error ? err : null,
        //     }),
        //   );
        // }
    }
    async _createSendTransport() {
        if (this._mediasoupDevice == null) {
            throw new Error("Peer._mediasoupDevice is null");
        }
        this.sendLock = this._initLock();
        let transportInfo;
        const args = {
            producing: true,
            consuming: false,
            rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
            maxBitrate: this.call.maxBitrate,
            appData: {
                producerStats: true,
            },
        };
        try {
            transportInfo = await this.call._request("createWebRtcTransport", args);
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            const vdcerr = new errors_deprecated_1.WSRequestError("createSendTransport: ws request error", {
                inner,
                request: "createWebRtcTransport",
                args,
                internalCall: this.call,
            });
            this.call.emit("error", vdcerr);
            throw vdcerr;
        }
        this._sendTransport = this._mediasoupDevice.createSendTransport({
            id: transportInfo.id,
            iceParameters: transportInfo.iceParameters,
            iceCandidates: transportInfo.iceCandidates,
            dtlsParameters: transportInfo.dtlsParameters,
            ...this._transportOptions(),
        });
        this.sendLock.resolve();
        this.ctx.logger.debug("ms3._createSendTransport: created", {
            transportInfo: transportInfo,
            deviceCapabilities: this._mediasoupDevice.rtpCapabilities,
        });
        this._handleTransportEvents(this._sendTransport, "send");
    }
    handleCreateSendTransport(transportInfo) {
        if (this._mediasoupDevice == null) {
            throw new Error("Peer._mediasoupDevice is null");
        }
        this._sendTransport = this._mediasoupDevice.createSendTransport({
            id: transportInfo.id,
            iceParameters: transportInfo.iceParameters,
            iceCandidates: transportInfo.iceCandidates,
            dtlsParameters: transportInfo.dtlsParameters,
            ...this._transportOptions(),
        });
        this.ctx.logger.debug("ms3._createSendTransport: created", {
            transportInfo: transportInfo,
            deviceCapabilities: this._mediasoupDevice.rtpCapabilities,
        });
        this._handleTransportEvents(this._sendTransport, "send");
    }
    async _createReceiveTransport() {
        if (this._mediasoupDevice == null) {
            throw new Error("Peer._mediasoupDevice is null");
        }
        this.recvLock = this._initLock();
        let transportInfo;
        const args = {
            forceTcp: this._forceRecvTcp,
            producing: false,
            consuming: true,
            rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        };
        // save if the call is closed/closing before entering try/catch block
        const closed = this._closed || this.call._closed;
        try {
            transportInfo = await this.call._request("createWebRtcTransport", args);
        }
        catch (err) {
            const currentlyClosed = this._closed || this.call._closed;
            //if the call was closed during async request sallow error
            if (!closed && currentlyClosed) {
                return;
            }
            const inner = err instanceof Error ? err : null;
            const vdcerr = new errors_deprecated_1.WSRequestError("createReceiveTransport: ws request error", {
                inner,
                request: "createWebRtcTransport",
                args,
                internalCall: this.call,
            });
            this.call.emit("error", vdcerr);
            throw vdcerr;
        }
        transportInfo = {
            ...transportInfo,
            ...this._transportOptions(),
        };
        this._recvTransport = this._mediasoupDevice.createRecvTransport(transportInfo);
        this.recvLock.resolve();
        this.ctx.logger.debug("ms3._createReceiveTransport: created", { transportInfo: transportInfo });
        this._handleTransportEvents(this._recvTransport, "recv");
    }
    handleCreateReceiveTransport(transportInfo) {
        if (this._mediasoupDevice == null) {
            throw new Error("Peer._mediasoupDevice is null");
        }
        transportInfo = {
            ...transportInfo,
            ...this._transportOptions(),
        };
        this._recvTransport = this._mediasoupDevice.createRecvTransport(transportInfo);
        this.ctx.logger.debug("ms3._createReceiveTransport: created", { transportInfo: transportInfo });
        this._handleTransportEvents(this._recvTransport, "recv");
    }
    // TODO: this should probably be returned from the SFU when the request to create a transport is made
    _transportOptions() {
        var _a, _b;
        const turnOrStun = adapter_1.device.isEdge ? this.call._edgeIceServers() : this.call._iceServers();
        this.call._iceTransportPolicyUsed =
            (_b = (_a = this.call._iceTransportPolicy) !== null && _a !== void 0 ? _a : this.call._localSettings.iceTransportPolicy) !== null && _b !== void 0 ? _b : "all";
        return {
            iceServers: turnOrStun,
            iceTransportPolicy: this.call._iceTransportPolicyUsed,
        };
    }
    _handleTransportEvents(transport, transportType) {
        // If the server is mis-configured no connection state changes occur so
        // we need to monitor manually for a change
        let failedIceTimeout;
        if ((transportType === "send" && this.call._requireSend) || (transportType === "recv" && this.call._requireRecv)) {
            failedIceTimeout = adapter_1.device.setTimeout(() => {
                if (transport.connectionState === "new") {
                    this.emitErrorDeprecated(new errors_deprecated_1.TransportStateError("Transport failed to change connection state", {
                        critical: true,
                    }));
                }
            }, 20000);
        }
        const DIR = transportType.toUpperCase();
        let prevState = null;
        transport.on("connectionstatechange", (state) => {
            adapter_1.device.clearTimeout(failedIceTimeout);
            if (DIR === "SEND") {
                this.call.emit("CALL_SET_SENDING_ICE_STATE", { state });
            }
            else {
                this.call.emit("CALL_SET_RECEIVING_ICE_STATE", { state });
            }
            this.ctx.logger.setMessageAggregate(`${transportType}TransportId`, transport.id);
            this.ctx.logger.setMessageAggregate(`${transportType}TransportState`, state);
            const connection = adapter_1.device.isImplements(feature_1.Feature.NETWORK_INFORMATION)
                ? adapter_1.device.connection
                : null;
            this.ctx.logger.info(`${transportType} transport connection state change`, {
                state,
                transportType,
                prevState,
                connection: connection,
            });
            prevState = state;
            // @hack: which browser has 'completed' state?
            if (state === "completed") {
                state = "connected";
            }
            switch (state) {
                case "connected":
                    transport.appData.onceConnected = true;
                    this.call._removeMessage(messageList_1.TROUBLESHOOTING.NETWORK_FIREWALLED);
                    this.call._removeMessage(messageList_1.TROUBLESHOOTING[DIR === "SEND" ? "WEBRTC_SEND_DISCONNECTED" : "WEBRTC_RECV_DISCONNECTED"]);
                    adapter_1.device.clearTimeout(this._iceTimeouts.get(transportType));
                    this._iceTimeouts.delete(transportType);
                    this.ctx.logger.info(`${transportType} transport entered connected state`);
                    this.emit("webrtc-transport-connected", transportType);
                    break;
                case "disconnected":
                    this.call._addMessage(messageList_1.TROUBLESHOOTING[DIR === "SEND" ? "WEBRTC_SEND_DISCONNECTED" : "WEBRTC_RECV_DISCONNECTED"]);
                    this._restartIce(transportType);
                    this.ctx.logger.warn(`${transportType} transport entered disconnected state`);
                    this.emit("webrtc-transport-disconnected", transportType);
                    break;
                case "failed":
                    this.ctx.logger.warn(`${transportType} transport entered failed state`);
                    this.emit("webrtc-transport-failed", transportType);
                    if (DIR === "SEND" && this.call._requireSend) {
                        this.emitErrorDeprecated(new errors_deprecated_1.TransportStateError("Send transport failed", { critical: true }));
                    }
                    else if (DIR === "RECV" && this.call._requireRecv) {
                        const err = new Error("Receive transport failed");
                        if (this.recvLock != null) {
                            this.recvLock.reject(err);
                        }
                        this.emitErrorDeprecated(new errors_deprecated_1.TransportStateError("Receive transport failed", { critical: true }));
                    }
                    else {
                        this.call._removeMessage(messageList_1.TROUBLESHOOTING[DIR === "SEND" ? "WEBRTC_SEND_DISCONNECTED" : "WEBRTC_RECV_DISCONNECTED"]);
                        this._restartIce(transportType);
                        if (!transport.appData.onceConnected) {
                            this.call._addMessage(messageList_1.TROUBLESHOOTING.NETWORK_FIREWALLED);
                        }
                    }
                    break;
                case "closed":
                    if (DIR === "send") {
                        if (this.sendLock != null) {
                            this.sendLock.reject(new Error("send transport closed"));
                        }
                    }
                    else if (this.recvLock != null) {
                        this.recvLock.reject(new Error("recv transport closed"));
                    }
                    if (!this._closed) {
                        this.emitErrorDeprecated(new errors_deprecated_1.TransportStateError(`${transportType} transport closed prematurely`, { critical: true }));
                    }
                    this.ctx.logger.warn(`${transportType} transport entered closed state`);
                    this.emit("webrtc-transport-closed", transportType);
                    break;
                default:
                    break;
            }
        });
        // @todo that using of a debug function must be removed vvv
        transport.observer.on("close", () => {
            // if (DIR === "send") {
            //   if (this.sendLock != null) {
            //     this.sendLock.reject(new Error("send transport closed"));
            //   }
            // } else if (this.recvLock != null) {
            //   this.recvLock.reject(new Error("recv transport closed"));
            // }
            // if (!this._closed) {
            //   this.emit("error", new Error(`${transportType} transport closed prematurely`));
            // }
            this.ctx.logger.info(`${transportType} transport.observer.closed state`);
            this.emit("webrtc-transport-closed", transportType);
        });
        // save if the call is closed/closing before entering try/catch block
        const closed = this._closed || this.call._closed;
        transport.on("connect", ({ dtlsParameters }, callback, errback) => {
            const args = {
                transportId: transport.id,
                dtlsParameters,
            };
            this.call
                ._request("connectWebRtcTransport", args)
                .then(() => {
                var _a;
                callback();
                (_a = this.recvLock) === null || _a === void 0 ? void 0 : _a.resolve();
                this.ctx.logger.debug("ms3._handleTransportEvents connected", {
                    transportType,
                });
            })
                .catch((err) => {
                const currentlyClosed = this._closed || this.call._closed;
                //if the call was closed during async request sallow error
                if (!closed && currentlyClosed) {
                    return;
                }
                const inner = err instanceof Error ? err : null;
                const vdcerr = new errors_deprecated_1.WSRequestError("handleTransportEvents: ws request error", {
                    inner,
                    request: "connectWebRtcTransport",
                    args,
                    internalCall: this.call,
                });
                this.call.emit("error", vdcerr);
                errback(vdcerr);
            });
        });
        transport.on("produce", ({ kind, rtpParameters, appData }, callback, errback) => {
            const args = {
                transportId: transport.id,
                kind,
                rtpParameters,
                appData,
            };
            this.call
                ._request("produce", args)
                .then((resp) => {
                callback(resp);
                this.ctx.logger.debug("ms3._handleTransportEvents: produce", {
                    kind,
                    appData: appData,
                });
            })
                .catch((err) => {
                const inner = err instanceof Error ? err : null;
                const vdcerr = new errors_deprecated_1.WSRequestError("handleTransportEvents: ws request error", {
                    inner,
                    request: "produce",
                    args,
                    internalCall: this.call,
                });
                this.call.emit("error", vdcerr);
                errback(vdcerr);
            });
        });
    }
    _addPeers(peers) {
        peers.forEach((peer) => {
            if (peer.id == null) {
                this.emitErrorDeprecated(new errors_deprecated_1.InternalError("peerId is undefined", {}));
                return;
            }
            this.peers.set(peer.id, peer);
            this.call.emit("CALL_ADD_PEER", { peer });
            this.call.emit("PEER_CHANGE", { peer, action: "added" });
        });
    }
    _restartIce(dir) {
        if (!this.call.transport.isConnected) {
            this.call.transport.once("connected", () => {
                this._restartIce(dir);
            });
            return;
        }
        const to = this._iceTimeouts.get(dir);
        if (to != null) {
            // ice restart already in progress
            return;
        }
        const restart = async () => {
            if (this._closed) {
                return;
            }
            this.ctx.logger.throttledLog("debug", 15000, "ice restart", { online: adapter_1.device.onLine });
            if (!adapter_1.device.onLine) {
                // retry until user is back online
                // clear timeout before setting in case there's one already in the map
                adapter_1.device.clearTimeout(this._iceTimeouts.get(dir));
                this._iceTimeouts.set(dir, adapter_1.device.setTimeout(restart.bind(this), 500));
                return;
            }
            try {
                const args = { dir };
                let params;
                try {
                    params = await this.call._request("restartIce", args);
                }
                catch (err) {
                    const inner = err instanceof Error ? err : null;
                    const vdcerr = new errors_deprecated_1.WSRequestError("restartIce: ws request error", {
                        inner,
                        request: "restartIce",
                        args,
                        internalCall: this.call,
                    });
                    this.call.emit("error", vdcerr);
                    throw vdcerr;
                }
                if (dir === "send") {
                    if (this._sendTransport == null) {
                        this.ctx.logger.error("call: restartIce error", { err: "_sendTransport is null" });
                        return;
                    }
                    await this._sendTransport.restartIce(params);
                }
                else {
                    if (this._recvTransport == null) {
                        this.ctx.logger.error("call: restartIce error", { err: "_recvTransport is null" });
                        return;
                    }
                    await this._recvTransport.restartIce(params);
                }
                this._iceTimeouts.delete(dir);
                return;
            }
            catch (err) {
                const msg = err instanceof Error ? err.message : "unknown error";
                this.ctx.logger.warn("call: restartIce error", { err: msg });
            }
            // timeout gets cleared once a candidate is found or call is closed.
            // clear timeout before setting in case there's one already in the map
            adapter_1.device.clearTimeout(this._iceTimeouts.get(dir));
            this._iceTimeouts.set(dir, adapter_1.device.setTimeout(restart.bind(this), 5000));
        };
        restart().catch(this.ctx.logger.error);
    }
    _onNewPeers(request) {
        this._addPeers(request.peers);
    }
    _onConsumerSources(request) {
        var _a, _b, _c;
        const streams = {};
        for (const c of request.consumers) {
            const ev = (_a = streams[`${c.peerId}-${c.streamName}`]) !== null && _a !== void 0 ? _a : {
                peerId: c.peerId,
                streamName: c.streamName,
                layers: [],
            };
            for (const enc of c.encodings) {
                ev.layers.push({
                    id: enc.id,
                    bitrate: (_c = (_b = enc.bitrate) !== null && _b !== void 0 ? _b : enc.maxBitrate) !== null && _c !== void 0 ? _c : 0,
                    isSource: !c.xcode,
                    appData: {
                        consumerId: c.consumerId,
                    },
                });
            }
            streams[`${c.peerId}-${c.streamName}`] = ev;
        }
        for (const ev of Object.values(streams)) {
            this.call.emit("CALL_CONSUMER_SOURCES", ev);
        }
    }
    async getLayers(streamName, peerId) {
        var _a, _b;
        let layers = [];
        let activeLayer = null;
        let sources = null;
        // // save if the call is closed/closing before entering try/catch block
        const closed = this._closed || this.call._closed;
        try {
            sources = await this.call.requestSources(streamName, peerId);
            this.ctx.logger.debug("request consumerSources from ws", { sources });
            // if (
            //   sources?.active?.encoding?.id != null &&
            //   sources?.active?.consumerId != null &&
            //   sources?.active?.xcode != null
            // ) {
            //   activeLayer = {
            //     id: sources.active.encoding.id,
            //     bitrate: sources.active.encoding.bitrate ?? sources.active.encoding.maxBitrate ?? 0,
            //     isSource: !sources.active.xcode,
            //     appData: {
            //       consumerId: sources.active.consumerId,
            //     },
            //   };
            // }
            for (const c of sources.consumers) {
                for (const e of c.encodings) {
                    const layer = {
                        id: e.id,
                        bitrate: (_b = (_a = e.bitrate) !== null && _a !== void 0 ? _a : e.maxBitrate) !== null && _b !== void 0 ? _b : 0,
                        isSource: !c.xcode,
                        appData: {
                            consumerId: c.consumerId,
                        },
                    };
                    layers.push(layer);
                    if (e.active) {
                        activeLayer = layer;
                    }
                }
            }
        }
        catch (err) {
            const currentlyClosed = this._closed || this.call._closed;
            //if the call was closed during async request sallow error
            if (!closed && currentlyClosed) {
                return [null, []];
            }
            this.ctx.logger.error(`unable to get consumer sources: ${err}`, { sources });
        }
        layers = (0, helper_1.sortEncodings)(layers);
        return [activeLayer, layers];
    }
    async _onNewConsumer(peer, request, response) {
        var _a, _b, _c, _d, _e, _f, _g;
        // TODO: check permissions
        this.ctx.logger.info("call: _onNewConsumer");
        if (this.recvLock != null) {
            await this.recvLock.ready();
        }
        else {
            await this._createReceiveTransport();
        }
        if (this._recvTransport == null) {
            this.ctx.logger.error("call: _onNewConsumer error", { err: "_recvTransport is null" });
            response(null, "_recvTransport is null");
            return;
        }
        if (this._recvTransport.closed) {
            await this._createReceiveTransport();
            await ((_a = this.recvLock) === null || _a === void 0 ? void 0 : _a.ready());
            response(null, "_recvTransport closed");
            return;
        }
        const consumer = await this._recvTransport.consume(request);
        // this a workaround for a bug in mediasoup when ms always create unpaused consumers
        if ((((_b = request.appData) === null || _b === void 0 ? void 0 : _b.trackEnabled) === false || request.producerPaused) && !consumer.paused) {
            consumer.pause();
        }
        (_c = peer.consumers) === null || _c === void 0 ? void 0 : _c.push(consumer);
        consumer.on("transportclose", () => {
            this.ctx.logger.debug("consumer event", {
                event: "transportclose",
                kind: request.kind,
            });
            this._deleteConsumers(peer, [consumer.id]);
        });
        consumer.on("trackended", () => {
            this.ctx.logger.debug("consumer event", {
                event: "trackended",
                kind: request.kind,
            });
            this._deleteConsumers(peer, [consumer.id]);
        });
        const { encodings } = consumer.rtpParameters;
        if (encodings == null || (encodings === null || encodings === void 0 ? void 0 : encodings.length) === 0) {
            this.ctx.logger.error("call: _onNewConsumer error", { err: "consumer doesn't have any encodings" });
            response(null, "consumer doesn't have any encodings");
            return;
        }
        const { spatialLayers, temporalLayers } = (0, mediasoup_client_1.parseScalabilityMode)((_e = (_d = encodings[0]) === null || _d === void 0 ? void 0 : _d.scalabilityMode) !== null && _e !== void 0 ? _e : "");
        const { streamName } = consumer.appData;
        if (typeof streamName !== "string") {
            this.ctx.logger.error("call: _onNewConsumer error", {
                err: "consumer.appData doesn't have a proper stream name",
            });
            response(null, "consumer.appData doesn't have a proper stream name");
            return;
        }
        if (peer.id == null) {
            this.ctx.logger.error("call: _onNewConsumer error", { err: "peer.id is null" });
            response(null, "peer.id is null");
            return;
        }
        const dontAdd = typeof request === "object" && request.dontAdd;
        // create specific pause and resume controls so that we
        // can notify the SFU
        const pause = () => {
            consumer.pause();
            const args = {
                consumerId: consumer.id,
            };
            this.call._request("pauseConsumer", args).catch((err) => {
                const inner = err instanceof Error ? err : null;
                const vdcerr = new errors_deprecated_1.WSRequestError("pause: ws request error", {
                    inner,
                    request: "pauseConsumer",
                    args,
                    internalCall: this.call,
                });
                this.call.emit("error", vdcerr);
            });
        };
        const resume = () => {
            consumer.resume();
            const args = {
                consumerId: consumer.id,
            };
            this.call._request("resumeConsumer", args).catch((err) => {
                const inner = err instanceof Error ? err : null;
                const vdcerr = new errors_deprecated_1.WSRequestError("resume: ws request error", {
                    inner,
                    request: "resumeConsumer",
                    args,
                    internalCall: this.call,
                });
                this.call.emit("error", vdcerr);
                return vdcerr;
            });
        };
        let [activeLayer, layers] = [null, []];
        if (!dontAdd) {
            // @todo: move it to CALL_ADD_PEER or something like that
            // save if the call is closed/closing before entering try/catch block
            const closed = this._closed || this.call._closed;
            if (consumer.kind === "video") {
                try {
                    [activeLayer, layers] = await this.getLayers(streamName, peer.id);
                }
                catch (err) {
                    const currentlyClosed = this._closed || this.call._closed;
                    //if the call was closed during async request sallow error
                    if (!closed && currentlyClosed) {
                        return;
                    }
                    throw err;
                }
            }
        }
        const ev = {
            peerId: peer.id,
            streamName,
            consumer: {
                id: consumer.id,
                peerId: peer.id,
                kind: consumer.kind,
                streamName: String(consumer.appData.streamName),
                supported: true, // TODO: consumer.supported not supported - if we have a consumer it should be supported
                locallyPaused: false,
                paused: consumer.paused,
                track: consumer.track,
                codec: (_g = (_f = consumer.rtpParameters.codecs[0]) === null || _f === void 0 ? void 0 : _f.mimeType.split("/")[1]) !== null && _g !== void 0 ? _g : "",
                pause,
                resume,
                layers,
                activeLayer,
                preferredSpatialLayer: spatialLayers - 1,
                preferredTemporalLayer: temporalLayers - 1,
                rtpParameters: consumer.rtpParameters,
                dontAdd,
            },
        };
        this.call.emit("CALL_ADD_CONSUMER", ev);
        if (!dontAdd) {
            // TODO: this seems unnecessary since we already have the track but it's required for the external api
            this.call.emit("CALL_SET_CONSUMER_TRACK", {
                peerId: peer.id,
                streamName,
                consumerId: consumer.id,
                track: consumer.track,
            });
            this.call.emit("PEER_CHANGE", { peer, action: "consumer_added" });
        }
        response({});
    }
    hasConsumerId(consumerId) {
        return Array.from(this.peers.values()).some((peerParam) => {
            var _a;
            return (_a = peerParam.consumers) === null || _a === void 0 ? void 0 : _a.some((consumer) => consumer.id === consumerId);
        });
    }
    _peerEvent(event, cb, errCb) {
        const transport = this.call.transport;
        const listener = async (req, resp) => {
            if (req.peerId == null || req.peerId === "") {
                this.ctx.logger.warn("invalid peer event request: no peerId", {
                    event,
                    req: req,
                });
                resp(null, "invalid peer event request: no peerId");
                return;
            }
            const peer = this.peers.get(req.peerId);
            if (peer == null) {
                this.ctx.logger.warn("peer not found for peer event", {
                    event,
                    req: req,
                });
                resp(null, "peer not found for peer event");
                return;
            }
            this.ctx.logger.debug("peer event", {
                event,
                req: req,
            });
            try {
                await cb(peer, req, resp);
            }
            catch (err) {
                errCb(err instanceof Error ? err : null, req);
                if (typeof resp === "function") {
                    if (err instanceof Error) {
                        resp(null, `peer event error: ${err.message}`);
                    }
                    else {
                        resp(null, "peer event error: undefined error");
                    }
                }
                else {
                    this.ctx.logger.debug(`BUG: check '${event}' event in PvcTransport, it doesn't provide 'resp' callback`, {
                        aggregates: { bug: true },
                    });
                }
            }
        };
        // @todo must be separated into request and message listeners
        transport.on(`request:${event}`, listener);
        transport.on(`message:${event}`, listener);
        this.addInnerDisposer(() => {
            transport.off(`request:${event}`, listener);
            transport.off(`message:${event}`, listener);
        });
    }
    async _onPeerClosed(peer) {
        var _a, _b;
        if (peer.id == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.InternalError("peerId is undefined", {}));
            return;
        }
        if (this.peers.has(peer.id)) {
            this.peers.delete(peer.id);
            const consumerIds = (_a = peer.consumers) === null || _a === void 0 ? void 0 : _a.map((consumer) => consumer.id);
            await this._deleteConsumers(peer, consumerIds !== null && consumerIds !== void 0 ? consumerIds : []);
            if (this.call._closed) {
                return;
            }
            this.call.emit("CALL_REMOVE_PEER", { peerId: (_b = peer.id) !== null && _b !== void 0 ? _b : "" });
            this.call.emit("PEER_CHANGE", { peer, action: "removed" });
        }
    }
    _producerEvent(event, cb, errCb) {
        const transport = this.call.transport;
        const listener = async (req) => {
            if (req.peerId !== this.call.peerId) {
                this.ctx.logger.debug("wrong peer ID for producer event", {
                    event,
                    req,
                });
                return;
            }
            const producer = this._producers.get(req.producerId);
            if (producer == null) {
                console.trace("producer not found for producer event", {
                    event,
                    req,
                });
                return;
            }
            try {
                await cb(producer, req);
            }
            catch (err) {
                errCb(err instanceof Error ? err : null, req);
            }
        };
        transport.on(`message:${event}`, listener);
        this.addInnerDisposer(() => {
            transport.off(`message:${event}`, listener);
        });
    }
    _consumerEvent(event, cb, errCb) {
        const transport = this.call.transport;
        const listener = async (req) => {
            var _a;
            const peer = this.peers.get(req.peerId);
            if (peer == null) {
                this.ctx.logger.debug("peer not found for consumer event", {
                    event,
                    req: req,
                });
                return;
            }
            const consumer = (_a = peer.consumers) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === req.consumerId);
            if (consumer == null) {
                if (event !== "consumerClosed") {
                    this.ctx.logger.debug("consumer not found for consumer event", {
                        event,
                        req: req,
                    });
                }
                return;
            }
            this.ctx.logger.debug("consumer event", {
                event,
                req: req,
            });
            try {
                await cb(consumer, peer, req);
            }
            catch (err) {
                errCb(err instanceof Error ? err : null, req);
            }
        };
        transport.on(`message:${event}`, listener);
        this.addInnerDisposer(() => {
            transport.off(`message:${event}`, listener);
        });
    }
    _deleteConsumers(peer, consumerIds) {
        consumerIds.forEach((id) => {
            var _a, _b, _c, _d;
            const consumer = (_a = peer.consumers) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === id);
            if (consumer == null) {
                return;
            }
            this.call.emit("CALL_REMOVE_CONSUMER", {
                peerId: (_b = peer.id) !== null && _b !== void 0 ? _b : "",
                streamName: String(consumer.appData.streamName),
                consumerId: consumer.id,
            });
            this.call.emit("PEER_CHANGE", { peer, action: "consumer_removed" });
            const i = (_c = peer.consumers) === null || _c === void 0 ? void 0 : _c.indexOf(consumer);
            (_d = peer.consumers) === null || _d === void 0 ? void 0 : _d.splice(i !== null && i !== void 0 ? i : 0, 1);
        });
        return Promise.resolve();
    }
    async _onConsumerClosed(consumer, peer) {
        consumer.close();
        const interval = this._consumerStatIntervals.get(consumer.id);
        clearInterval(interval);
        this._consumerStatIntervals.delete(consumer.id);
        await this._deleteConsumers(peer, [consumer.id]);
    }
    _onConsumerPaused(consumer, peer) {
        var _a;
        consumer.pause();
        this.call.emit("CALL_SET_CONSUMER_PAUSED", {
            peerId: (_a = peer.id) !== null && _a !== void 0 ? _a : "",
            streamName: String(consumer.appData.streamName),
            consumerId: consumer.id,
            originator: "peer",
        });
        return Promise.resolve();
    }
    _onConsumerResumed(consumer, peer) {
        var _a;
        consumer.resume();
        this.call.emit("CALL_SET_CONSUMER_RESUMED", {
            peerId: (_a = peer.id) !== null && _a !== void 0 ? _a : "",
            streamName: String(consumer.appData.streamName),
            consumerId: consumer.id,
            originator: "peer",
        });
        return Promise.resolve();
    }
    async _onConsumerScore(consumer, peer) {
        // TODO: Don't currently have this feature - need to create event and emit
    }
    async _onConsumerLayersChanged(consumer, peer, req) {
        this.call.emit("CALL_CONSUMER_LAYER_CHANGED", req);
        return Promise.resolve();
    }
    async _onSwitchConsumerTrack(peer, req, resp) {
        var _a, _b, _c, _d;
        const peerId = peer.id;
        if (peerId == null) {
            return;
        }
        const preparedConsumers = new Map();
        for (const rc of req.add) {
            const consumer = (_a = peer.consumers) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === rc.consumerId);
            if (!consumer) {
                const err = new errors_deprecated_1.SFUSwitchConsumerTrackEventError("unable to switch tracks: consumer not found", {
                    consumerId: rc.consumerId,
                    peer: this,
                    request: req,
                });
                this.emitErrorDeprecated(err);
                return;
            }
            const streamName = String(consumer.appData.streamName);
            if (!preparedConsumers.has(streamName)) {
                preparedConsumers.set(streamName, { add: [], remove: [] });
            }
            (_b = preparedConsumers.get(streamName)) === null || _b === void 0 ? void 0 : _b.add.push(consumer.track);
        }
        for (const rc of req.remove) {
            const consumer = (_c = peer.consumers) === null || _c === void 0 ? void 0 : _c.find((c) => c.id === rc.consumerId);
            if (!consumer) {
                const err = new errors_deprecated_1.SFUSwitchConsumerTrackEventError("unable to switch tracks: consumer not found", {
                    consumerId: rc.consumerId,
                    peer: this,
                    request: req,
                });
                this.emitErrorDeprecated(err);
                return;
            }
            const streamName = String(consumer.appData.streamName);
            if (!preparedConsumers.has(streamName)) {
                preparedConsumers.set(streamName, { add: [], remove: [] });
            }
            (_d = preparedConsumers.get(streamName)) === null || _d === void 0 ? void 0 : _d.remove.push(consumer.track);
        }
        for (const [streamName, consumers] of preparedConsumers.entries()) {
            if (consumers.add.length === 0 || consumers.remove.length === 0) {
                const err = new errors_deprecated_1.SFUSwitchConsumerTrackEventError("unable to switch tracks: consumers have different stream names", {
                    peer: this,
                    request: req,
                });
                this.emitErrorDeprecated(err);
                return;
            }
            this.call.emit("CALL_SWAP_CONSUMERS", {
                peerId,
                streamName,
                add: consumers.add,
                remove: consumers.remove,
            });
        }
        resp({});
    }
    _onProducerClosed(producer, req) {
        if (producer.closed) {
            return Promise.resolve();
        }
        this.call.emit("CALL_REMOVE_PRODUCER", {
            streamName: req.streamName,
            producerId: req.producerId,
            originator: "client",
        });
        producer.close();
        return Promise.resolve();
    }
    _onProducerPaused(producer) {
        producer.pause();
        return Promise.resolve();
    }
    _onProducerResumed(producer) {
        producer.resume();
        return Promise.resolve();
    }
    // TODO: don't have profile change - this._emit(events.setConsumerEffectiveProfile(consumer.peer.name, consumer.appData.streamName, consumer.id, profile));
    async produce(track, options, appData) {
        if (this.sendLock != null) {
            try {
                await this.sendLock.ready();
            }
            catch (ex) {
                throw new Error("unable to acquire a lock");
            }
        }
        else {
            try {
                await this._createSendTransport();
            }
            catch (err) {
                const msg = err instanceof Error ? err.message : "unknown error";
                this.ctx.logger.error("unable to create send transport", { err: msg });
                throw err;
            }
        }
        if (this._sendTransport == null) {
            const err = new Error("_sendTransport is null");
            this.ctx.logger.error("ms3.produce error", { err: err.message });
            throw err;
        }
        // If simulcast options are present, use encodings option with maxBitrate settings
        let producer;
        const { simulcast: { maxBitrates } = {} } = options;
        // MediaStreamTrack.kind returns a string set to "video" if it is a video track. Only set simulcast options for video tracks.
        if (maxBitrates && track.kind === "video") {
            const opts = {
                ...options,
                track,
                appData,
                // We only support two maxBitrates
                encodings: [
                    { maxBitrate: maxBitrates[0], dtx: true },
                    { maxBitrate: maxBitrates[1], dtx: true },
                ],
                stopTracks: false,
            };
            if (options.audioOnly) {
                opts.appData.audioOnly = options.audioOnly;
            }
            if (options.videoOnly) {
                opts.appData.videoOnly = options.videoOnly;
            }
            opts.appData.trackEnabled = opts.track.enabled;
            opts.appData.userId = this.call.user.userId;
            opts.appData.displayName = this.call.user.displayName;
            this.ctx.logger.debug("create maxBitrates producer", {
                ...opts,
                track: null,
                appData: null,
            });
            producer = await this._sendTransport.produce(opts);
        }
        else {
            const opts = {
                ...options,
                track,
                appData,
                stopTracks: false,
            };
            if (options.audioOnly) {
                opts.appData.audioOnly = options.audioOnly;
            }
            if (options.videoOnly) {
                opts.appData.videoOnly = options.videoOnly;
            }
            opts.appData.trackEnabled = opts.track.enabled;
            opts.appData.userId = this.call.user.userId;
            opts.appData.displayName = this.call.user.displayName;
            this.ctx.logger.debug("create basic producer", {
                ...opts,
                track: null,
                appData: null,
            });
            if (opts.encodings && opts.encodings.length > 1 && adapter_1.device.isIosDevice && adapter_1.device.isChrome) {
                opts.encodings = [{ maxBitrate: 2500000 }];
                this.emitError((0, errors_1.createError)(error_1.ErrorCode.SimulcastDisabled, "simulcast is disabled due to ios chrome limitations", {}));
            }
            producer = await this._sendTransport.produce(opts);
        }
        this._producers.set(producer.id, producer); // not sure if we need to keep a ref
        this.ctx.logger.debug("ms3.produce", {
            appData: appData,
            trackLabel: track.label,
        });
        producer.on("transportclose", () => {
            this.ctx.logger.debug("ms3.produce: transport closed", {
                trackLabel: track.label,
                appData: appData,
            });
        });
        producer.on("trackended", () => {
            this.ctx.logger.debug("ms3.produce: track ended", {
                trackLabel: track.label,
                appData: appData,
            });
        });
        return producer;
    }
    clear(debugString) {
        this._iceTimeouts.forEach((to) => {
            adapter_1.device.clearTimeout(to);
        });
        this._iceTimeouts.clear();
        if (this._recvTransport != null) {
            this._recvTransport.close();
        }
        if (this._sendTransport != null) {
            this._sendTransport.close();
        }
    }
    async close(debugString = "Implementer did not pass debugString", internal = false) {
        if (this._closed) {
            return;
        }
        this.clear(debugString);
        this._closed = true;
        // This isnt necessary but makes the reason cleaner when it has a bunch of strings appended to it.
        let reason = "Reason for close not given";
        const playerString = "PlayerUiState inner disposer:";
        if (debugString) {
            const removePlayerString = debugString.indexOf(playerString);
            if (removePlayerString !== -1) {
                reason = debugString.substring(removePlayerString + playerString.length).trim();
            }
            else {
                reason = debugString;
            }
        }
        try {
            await this.call.transport.request("closePeer", { internal, reason });
        }
        catch (err) {
            const msg = err instanceof Error ? err.toString() : "unknown error";
            this.ctx.logger.info("ms3.close: error closing peer on the server", {
                aggregates: { err: msg, internal, reason: debugString },
            });
        }
    }
    _initLock() {
        const lock = {};
        lock.promise = new Promise((resolve, reject) => {
            lock.resolve = () => {
                lock.resolved = true;
                resolve();
            };
            lock.reject = (err) => {
                if (lock.resolved) {
                    lock.promise = Promise.reject(err);
                }
                else {
                    reject(err);
                }
            };
        });
        lock.ready = () => lock.promise;
        return lock;
    }
    toJSON() {
        return {
            peers: Array.from(this.peers.values()).map((p) => {
                return {
                    id: p.id,
                    name: p.name,
                    peerId: p.peerId,
                    userId: p.userId,
                    displayName: p.displayName,
                    scope: p.scope,
                    permissions: p.permissions,
                    behaviours: p.behaviours,
                };
            }),
            aggregates: {
                support: this.ctx.support.hash,
                localPeerId: this.call._localPeerId,
            },
        };
    }
}
exports.Peer = Peer;
Peer.displayName = "PvcPeer";
//# sourceMappingURL=peer.js.map